import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  CreditCard,
  Plus,
  Trash2,
  Star,
  StarOff,
  Loader2,
  Shield,
  AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Card brand icons/colors
const cardBrandStyles = {
  Visa: { bg: 'bg-blue-600', text: 'VISA' },
  Mastercard: { bg: 'bg-red-500', text: 'MC' },
  Amex: { bg: 'bg-blue-400', text: 'AMEX' },
  Discover: { bg: 'bg-orange-500', text: 'DISC' },
  default: { bg: 'bg-gray-600', text: 'CARD' }
};

// Detect card brand from number
const detectCardBrand = (number) => {
  const cleaned = number.replace(/\s/g, '');
  if (cleaned.startsWith('4')) return 'Visa';
  if (cleaned.startsWith('5')) return 'Mastercard';
  if (cleaned.startsWith('3')) return 'Amex';
  if (cleaned.startsWith('6')) return 'Discover';
  return 'Visa';
};

// Format card number with spaces
const formatCardNumber = (value) => {
  const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
  const matches = v.match(/\d{4,16}/g);
  const match = (matches && matches[0]) || '';
  const parts = [];
  for (let i = 0, len = match.length; i < len; i += 4) {
    parts.push(match.substring(i, i + 4));
  }
  if (parts.length) {
    return parts.join(' ');
  }
  return value;
};

// Add Card Form Component
const AddCardForm = ({ onSuccess, onCancel }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [setAsDefault, setSetAsDefault] = useState(true);
  const [error, setError] = useState(null);
  const [cardNumber, setCardNumber] = useState('');
  const [expMonth, setExpMonth] = useState('');
  const [expYear, setExpYear] = useState('');
  const [cvc, setCvc] = useState('');

  const handleCardNumberChange = (e) => {
    const formatted = formatCardNumber(e.target.value);
    if (formatted.replace(/\s/g, '').length <= 16) {
      setCardNumber(formatted);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate inputs
    const cleanNumber = cardNumber.replace(/\s/g, '');
    if (cleanNumber.length < 13) {
      setError(t('paymentMethods.invalidCardNumber') || 'Please enter a valid card number');
      return;
    }
    if (!expMonth || !expYear) {
      setError(t('paymentMethods.invalidExpiry') || 'Please enter expiration date');
      return;
    }
    if (cvc.length < 3) {
      setError(t('paymentMethods.invalidCvc') || 'Please enter a valid CVC');
      return;
    }

    setLoading(true);

    try {
      // Send to backend (demo mode)
      await axios.post(`${API}/payment-methods/add-card`, {
        card_number: cleanNumber,
        exp_month: parseInt(expMonth),
        exp_year: parseInt(expYear) + 2000, // Convert YY to YYYY
        brand: detectCardBrand(cleanNumber),
        set_as_default: setAsDefault
      });

      toast.success(t('paymentMethods.cardAddedSuccess'));
      onSuccess();

    } catch (err) {
      console.error('Error adding card:', err);
      setError(err.response?.data?.detail || t('paymentMethods.failedToAddCard'));
    } finally {
      setLoading(false);
    }
  };

  const detectedBrand = detectCardBrand(cardNumber);
  const brandStyle = cardBrandStyles[detectedBrand] || cardBrandStyles.default;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="space-y-4">
        {/* Card Number */}
        <div>
          <Label htmlFor="cardNumber">{t('paymentMethods.cardNumber')}</Label>
          <div className="relative mt-1">
            <Input
              id="cardNumber"
              type="text"
              value={cardNumber}
              onChange={handleCardNumberChange}
              placeholder="1234 5678 9012 3456"
              className="pr-16"
              maxLength={19}
            />
            <div className={`absolute right-2 top-1/2 -translate-y-1/2 ${brandStyle.bg} text-white text-xs font-bold px-2 py-1 rounded`}>
              {brandStyle.text}
            </div>
          </div>
        </div>

        {/* Expiry and CVC */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>{t('paymentMethods.expiryDate')}</Label>
            <div className="flex gap-2 mt-1">
              <Input
                type="text"
                value={expMonth}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  if (v.length <= 2 && (v === '' || parseInt(v) <= 12)) {
                    setExpMonth(v);
                  }
                }}
                placeholder="MM"
                maxLength={2}
                className="w-16 text-center"
              />
              <span className="flex items-center text-gray-400">/</span>
              <Input
                type="text"
                value={expYear}
                onChange={(e) => {
                  const v = e.target.value.replace(/\D/g, '');
                  if (v.length <= 2) {
                    setExpYear(v);
                  }
                }}
                placeholder="YY"
                maxLength={2}
                className="w-16 text-center"
              />
            </div>
          </div>
          <div>
            <Label htmlFor="cvc">CVC</Label>
            <Input
              id="cvc"
              type="text"
              value={cvc}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '');
                if (v.length <= 4) {
                  setCvc(v);
                }
              }}
              placeholder="123"
              maxLength={4}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
          <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      <div className="flex items-center space-x-2">
        <Checkbox 
          id="setDefault" 
          checked={setAsDefault} 
          onCheckedChange={setSetAsDefault}
        />
        <Label htmlFor="setDefault" className="text-sm text-gray-600 cursor-pointer">
          {t('paymentMethods.setAsDefault')}
        </Label>
      </div>

      <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-2">
        <Shield className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-green-700">
          {t('paymentMethods.secureNote')}
        </p>
      </div>

      {/* Demo Mode Notice */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
        <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-amber-700">
          <strong>{t('paymentMethods.demoMode')}:</strong> {t('paymentMethods.demoModeDescription')}
        </p>
      </div>

      <div className="flex gap-3 pt-2">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          {t('common.cancel')}
        </Button>
        <Button
          type="submit"
          disabled={loading}
          className="flex-1 bg-teal-600 hover:bg-teal-700"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              {t('paymentMethods.addingCard')}
            </>
          ) : (
            <>
              <Plus className="h-4 w-4 mr-2" />
              {t('paymentMethods.addCard')}
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

// Main Payment Methods Component
const PaymentMethods = ({ onUpdate }) => {
  const { t } = useTranslation();
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddCard, setShowAddCard] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [settingDefaultId, setSettingDefaultId] = useState(null);

  useEffect(() => {
    fetchPaymentMethods();
  }, []);

  const fetchPaymentMethods = async () => {
    try {
      const response = await axios.get(`${API}/payment-methods/`);
      setPaymentMethods(response.data);
    } catch (error) {
      console.error('Error fetching payment methods:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefault = async (paymentMethodId) => {
    setSettingDefaultId(paymentMethodId);
    try {
      await axios.post(`${API}/payment-methods/set-default`, {
        payment_method_id: paymentMethodId
      });
      toast.success(t('paymentMethods.defaultUpdated'));
      fetchPaymentMethods();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error setting default:', error);
      toast.error(error.response?.data?.detail || t('paymentMethods.failedToSetDefault'));
    } finally {
      setSettingDefaultId(null);
    }
  };

  const handleDelete = async (paymentMethodId) => {
    if (!confirm(t('paymentMethods.confirmDelete'))) {
      return;
    }

    setDeletingId(paymentMethodId);
    try {
      await axios.delete(`${API}/payment-methods/${paymentMethodId}`);
      toast.success(t('paymentMethods.cardDeleted'));
      fetchPaymentMethods();
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error deleting card:', error);
      toast.error(error.response?.data?.detail || t('paymentMethods.failedToDelete'));
    } finally {
      setDeletingId(null);
    }
  };

  const handleCardAdded = () => {
    setShowAddCard(false);
    fetchPaymentMethods();
    if (onUpdate) onUpdate();
  };

  const getCardStyle = (brand) => {
    return cardBrandStyles[brand] || cardBrandStyles.default;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
          <p className="text-sm text-gray-500 mt-2">{t('paymentMethods.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-teal-600" />
                {t('paymentMethods.title')}
              </CardTitle>
              <CardDescription>{t('paymentMethods.description')}</CardDescription>
            </div>
            <Button
              onClick={() => setShowAddCard(true)}
              className="bg-teal-600 hover:bg-teal-700"
              size="sm"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('paymentMethods.addCard')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {paymentMethods.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-lg">
              <CreditCard className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600 font-medium">{t('paymentMethods.noCards')}</p>
              <p className="text-sm text-gray-500 mt-1">{t('paymentMethods.addFirstCard')}</p>
              <Button
                onClick={() => setShowAddCard(true)}
                variant="outline"
                className="mt-4"
              >
                <Plus className="h-4 w-4 mr-2" />
                {t('paymentMethods.addCard')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {paymentMethods.map((pm) => {
                const style = getCardStyle(pm.brand);
                return (
                  <div
                    key={pm.id}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 transition-colors ${
                      pm.is_default ? 'border-teal-300 bg-teal-50' : 'border-gray-200 bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {/* Card Brand Badge */}
                      <div className={`w-12 h-8 ${style.bg} rounded flex items-center justify-center text-white text-xs font-bold`}>
                        {style.text}
                      </div>
                      
                      {/* Card Info */}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-semibold text-gray-900">
                            •••• •••• •••• {pm.last4}
                          </p>
                          {pm.is_default && (
                            <Badge className="bg-teal-600 text-xs">
                              <Star className="h-3 w-3 mr-1" />
                              {t('paymentMethods.default')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {t('paymentMethods.expires')} {pm.exp_month.toString().padStart(2, '0')}/{pm.exp_year.toString().slice(-2)}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {!pm.is_default && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleSetDefault(pm.id)}
                          disabled={settingDefaultId === pm.id}
                          className="text-gray-600 hover:text-teal-600"
                        >
                          {settingDefaultId === pm.id ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <StarOff className="h-4 w-4 mr-1" />
                              {t('paymentMethods.makeDefault')}
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(pm.id)}
                        disabled={deletingId === pm.id}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        {deletingId === pm.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Card Dialog */}
      <Dialog open={showAddCard} onOpenChange={setShowAddCard}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-teal-600" />
              {t('paymentMethods.addNewCard')}
            </DialogTitle>
            <DialogDescription>
              {t('paymentMethods.addCardDescription')}
            </DialogDescription>
          </DialogHeader>
          
          <AddCardForm 
            onSuccess={handleCardAdded}
            onCancel={() => setShowAddCard(false)}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PaymentMethods;
